import dayjs from 'dayjs';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { ConfirmPrompt } from '../../Abstracts/Components';
import useConfirmPrompt from '../../Hooks/UseConfirmPrompt';
import { NextBooking, PublicLiabilityInsuranceWithDocuments } from '../../Services/InsuranceLicenseService';
import { ErrorHandler } from '../../Services/UserApi';
import { determineInsuranceValidity } from './InsuranceLicenseUtils';
import InsuranceLicenseAddCta from './InsuranceLicenseAddCta';
import LiabilityInsuranceTable from './LiabilityInsuranceTable';

type Props = {
	policies: PublicLiabilityInsuranceWithDocuments[];
	nextBookings: NextBooking[];
	removeLiabilityInsurance: (policyId: number) => Promise<unknown>;
};

export default function LiabilityInsuranceSummarySection(props: Props) {
	const { policies, nextBookings, removeLiabilityInsurance } = props;
	const [removingPolicyId, setRemovingPolicyId] = useState<number | null>(null);
	const [policyIdToDelete, setPolicyIdToDelete] = useState<number | null>(null);
	const nowUnix = dayjs().unix();

	let hasValidPolicy = false;
	const firstBookingDate = Number(nextBookings[0]?.MarketDayDate ?? nowUnix);
	const secondBookingDate = Number(nextBookings[1]?.MarketDayDate ?? nowUnix);
	switch (nextBookings.length) {
		case 0:
			hasValidPolicy = determineInsuranceValidity(nowUnix, policies);
			break;
		case 1:
			hasValidPolicy = determineInsuranceValidity(firstBookingDate, policies);
			break;
		default:
			hasValidPolicy =
				determineInsuranceValidity(firstBookingDate, policies) &&
				determineInsuranceValidity(secondBookingDate, policies);
			break;
	}

	const warning =
		policies.length === 0
			? 'We have no record of any valid public liability insurance for your stall. Please add a policy using the button above.'
			: !hasValidPolicy
				? "You don't have a valid insurance policy for an upcoming booking. Please add a valid policy using the button above."
				: '';
	const firstBookingForStatus = nextBookings[0] ?? { MarketDayDate: nowUnix };
	const firstBooking = nextBookings[0];
	const secondBooking = nextBookings[1];

	const deletePolicyPrompt = useConfirmPrompt({
		title: 'Remove Insurance Policy',
		text: 'Are you sure you want to remove this insurance policy? This action cannot be undone.',
		icon: 'fa fa-exclamation-triangle',
		confirmButton: {
			text: 'Remove',
			color: 'danger',
			icon: 'fa fa-trash',
		},
		onConfirm: async () => {
			if (policyIdToDelete === null) {
				return;
			}
			setRemovingPolicyId(policyIdToDelete);
			try {
				await removeLiabilityInsurance(policyIdToDelete);
				toast.success('Insurance policy removed');
			} catch (error) {
				ErrorHandler.toast(error);
				throw error;
			} finally {
				setRemovingPolicyId(null);
				setPolicyIdToDelete(null);
			}
		},
	});

	const onRemovePolicy = (policyId: number) => {
		setPolicyIdToDelete(policyId);
		deletePolicyPrompt.open();
	};

	return (
		<section className="insurance-license-section">
			<h3>Public liability insurance</h3>
			<div className="card-note">Every stallholder requires a valid public liability insurance policy.</div>

			<div className="insurance-license-card">
				<InsuranceLicenseAddCta
					to="/add/public-liability-insurance"
					text="Add public liability insurance policy"
				/>

				{warning && (
					<div className="liability-insurance-warning-region">
						<span className="card-warning">{warning}</span>
					</div>
				)}

				{policies.length > 0 && (
					<LiabilityInsuranceTable
						policies={policies}
						firstBookingForStatus={firstBookingForStatus}
						firstBooking={firstBooking}
						secondBooking={secondBooking}
						removingPolicyId={removingPolicyId}
						onRemovePolicy={onRemovePolicy}
					/>
				)}
			</div>
			<ConfirmPrompt
				open={deletePolicyPrompt.isOpen}
				onClose={deletePolicyPrompt.close}
				{...deletePolicyPrompt.promptProps}
			/>
		</section>
	);
}
